import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import { entitlementControls } from './entitlementcontrols'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside } from './writes'

/**
 * Risk › Entitlement matrix (`rk-entitlements`), from TWO reads:
 * `GET /v1/admin/entitlements` and `GET /v1/admin/compliance-locks`.
 *
 * # Two layers, two tables, two routes — and they must not be merged
 *
 * This is the whole shape of the screen and it is law 16 made structural:
 *
 * > *"An operational rule carries `enabled` and can therefore permit as well as
 * > refuse. A compliance lock has NO SUCH COLUMN AT ALL — there is no value
 * > anybody can write, at any role, that makes a lock grant something — so a
 * > merged list would need an `enabled` field that is always false on half its
 * > rows and that somebody would eventually try to set."*
 *
 * So there are two tables here, never one, and the lock table has no `enabled`
 * column to render. Nothing in this module maps one row shape onto the other.
 *
 * # This console does NOT resolve what a customer may do, and must not
 *
 * Filtering by customer returns the rows scoped TO that customer, never the
 * rows that GOVERN them: the global rows and every segment they belong to apply
 * as well, and combining them is a precedence rule — any live lock at any scope
 * refuses, however specific an operational rule is.
 *
 * `internal/entitlement` is the only implementation of that rule and it runs on
 * the money path, where the answer binds. *"A console that re-derived it here
 * would be a second implementation kept in step by attention."* So this screen
 * shows rows and states that it is not an answer.
 *
 * # An absent row is a refusal, not an unknown
 *
 * A (method, direction) pair with no rule governing the instant resolves to
 * DISABLED — the same fail-closed posture invariant 7 takes for a missing
 * jurisdiction. A sparse matrix is not one waiting to be filled in: every cell
 * with no row is already an answer, and it is `no`. The corpus screen drew a
 * dense grid of green cells and no locks at all.
 *
 * # Closed rows are returned and are not an artefact
 *
 * Migration 099 refuses to delete or edit either table: dropping a closed row
 * *"destroys the evidence that a restriction was ever imposed (invariant 9)"*.
 * A view showing only what is in force cannot answer *"was this customer
 * blocked in March"*, which is what an effective window exists for. So this
 * screen renders history and marks what is live rather than hiding the rest —
 * law 5's append-only posture on a table nobody may edit.
 *
 * # `lift_permission` is a FACT ON THE ROW, not something derived here
 *
 * The lock read serves the permission a principal needs to lift each lock. Law
 * 16 greys the control for an Ops principal, and this is what it greys from —
 * rather than this console carrying its own model of the role nest, which would
 * be a second copy of the authorisation rule on the least reliable surface.
 */

export const ENTITLEMENT_RULES_PATH = '/entitlements'
export const COMPLIANCE_LOCKS_PATH = '/compliance-locks'

const SOURCE =
  'postgres · entitlement_rules + compliance_locks (GET /v1/admin/entitlements, /compliance-locks)'
const AUTHORITY = 'operational'

/** The window and its attribution, shared by both row shapes. */
interface Window {
  readonly effectiveFrom: string
  readonly effectiveTo: string
  /** SERVED. `effective_to === null` is one negation from meaning the opposite. */
  readonly live: boolean | null
  readonly createdAt: string
  readonly createdBy: string
  readonly reasonCode: string
  readonly reason: string
  readonly closedBy: string
  readonly closeReasonCode: string
  readonly closeReason: string
}

interface Scope {
  readonly scope: string
  readonly segmentKey: string
  readonly customerId: number | null
}

interface Rule {
  readonly id: number | null
  readonly scope: Scope
  readonly methodCode: string
  readonly direction: string
  /** The field a lock does not have and cannot grow. */
  readonly enabled: boolean | null
  readonly window: Window
}

interface Lock {
  readonly id: number | null
  readonly scope: Scope
  readonly methodCode: string
  readonly direction: string
  /** Who imposed it, which decides the remedy. Never defaulted by the backend. */
  readonly source: string
  readonly window: Window
  /** The permission needed to lift THIS lock, as a fact on the row. */
  readonly liftPermission: string
}

function readWindow(o: Record<string, unknown>): Window {
  return {
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
    createdAt: str(o['created_at']),
    createdBy: str(o['created_by']),
    reasonCode: str(o['reason_code']),
    reason: str(o['reason']),
    closedBy: str(o['closed_by']),
    closeReasonCode: str(o['close_reason_code']),
    closeReason: str(o['close_reason']),
  }
}

function readScope(o: Record<string, unknown>): Scope {
  return {
    scope: str(o['scope']),
    segmentKey: str(o['segment_key']),
    customerId: int(o['customer_id']),
  }
}

function readRule(raw: unknown): Rule | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const methodCode = str(o['method_code'])
  if (methodCode === '') return null
  return {
    id: int(o['id']),
    scope: readScope(o),
    methodCode,
    direction: str(o['direction']),
    enabled: typeof o['enabled'] === 'boolean' ? o['enabled'] : null,
    window: readWindow(o),
  }
}

function readLock(raw: unknown): Lock | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const methodCode = str(o['method_code'])
  if (methodCode === '') return null
  return {
    id: int(o['id']),
    scope: readScope(o),
    methodCode,
    direction: str(o['direction']),
    source: str(o['source']),
    window: readWindow(o),
    liftPermission: str(o['lift_permission']),
  }
}

/**
 * Who a row is about.
 *
 * The subject is three fields on the wire rather than one polymorphic string,
 * *"so a console cannot render `segment: 7`"*. It is assembled here and nowhere
 * else, so the two tables cannot spell it differently.
 */
function scopeCell(s: Scope): Cell {
  switch (s.scope) {
    case 'global': return 'everybody'
    case 'segment': return s.segmentKey === '' ? 'segment — NOT NAMED' : `segment ${s.segmentKey}`
    case 'customer':
      return s.customerId === null ? 'customer — NOT NAMED' : `customer ${String(s.customerId)}`
    case '': return '— not stated'
    default: return s.scope
  }
}

/**
 * Whether the row governs now.
 *
 * `live` is served rather than derived from `effective_to` being absent. The
 * derivation is one negation from meaning the opposite, and this is the field a
 * screen greys a row on.
 */
function liveCell(w: Window): ChipCell {
  if (w.live === null) return ['— not stated', 'r']
  return w.live ? ['in force', 'a'] : ['closed', 'n']
}

/** Who wrote the row, and what they said. */
function authorCell(w: Window): Cell {
  const who = w.createdBy === '' ? '— unattributed' : w.createdBy
  return w.reasonCode === '' ? who : `${who} · ${w.reasonCode}`
}

/**
 * Who closed the row, and when nobody did.
 *
 * All three close fields are present together or absent together — a lift with
 * nobody's name on it is unrepresentable in the schema (099's
 * `close_attributed` CHECK). So an in-force row says so, and a closed row with
 * no name is a shape worth reporting rather than blanking.
 */
function closedCell(w: Window): Cell {
  if (w.live === true) return '— still in force'
  if (w.closedBy === '') return w.live === null ? '—' : '— CLOSED WITH NO NAME ON IT'
  const when = w.effectiveTo === '' ? '' : ` · ${day(w.effectiveTo)}`
  return `${w.closedBy}${w.closeReasonCode === '' ? '' : ` · ${w.closeReasonCode}`}${when}`
}

const RULE_COLS: readonly Column[] = [
  ['Who', 'x', 'minmax(170px,1.2fr)'],
  ['Method', 'm', '150px'],
  ['Direction', 'c', '110px'],
  ['Permits', 'c', '140px'],
  ['In force', 'c', '110px'],
  ['From', 'm', '116px'],
  ['Set by', 'x', 'minmax(160px,1.2fr)'],
  ['Closed by', 'x', 'minmax(170px,1.2fr)'],
]

/**
 * The lock columns, and the one that is structurally absent.
 *
 * There is no "Permits" column here and there is no code path that could fill
 * one. `compliance_locks` has no `enabled` column, so a lock can only ever
 * refuse — a guarantee of the schema, carried by the response shape rather than
 * described in a note beside it.
 */
const LOCK_COLS: readonly Column[] = [
  ['Who', 'x', 'minmax(170px,1.2fr)'],
  ['Method', 'm', '150px'],
  ['Direction', 'c', '110px'],
  ['Imposed by', 'c', '170px'],
  ['In force', 'c', '110px'],
  ['From', 'm', '116px'],
  ['Set by', 'x', 'minmax(160px,1.2fr)'],
  ['Lifted by', 'x', 'minmax(170px,1.2fr)'],
  ['Lifting needs', 'm', '170px'],
]

function directionCell(d: string): ChipCell {
  switch (d) {
    case 'in': return ['inbound', 'n']
    case 'out': return ['outbound', 'n']
    case '': return ['— not stated', 'r']
    // `both` is a rail CAPABILITY and never an entitlement: a row meaning both
    // could not be superseded one direction at a time.
    default: return [`${d} — not an entitlement direction`, 'r']
  }
}

function enabledCell(r: Rule): ChipCell {
  if (r.enabled === null) return ['— not stated', 'r']
  return r.enabled ? ['allowed', 'g'] : ['refused', 'n']
}

/**
 * Who imposed a lock, which decides the remedy rather than merely labelling it.
 *
 * An EMI-imposed lock is lifted by the institution changing its mind, a
 * regulator-imposed one is not lifted by us at all, and an internal one is a
 * decision this programme can revisit. The backend never defaults this field
 * because *"the row is what an institutional audit reads to find out on whose
 * authority a customer was restricted"*.
 */
function sourceCell(l: Lock): ChipCell {
  switch (l.source) {
    case 'emi': return ['our institution', 'r']
    case 'regulator': return ['a regulator', 'r']
    case 'internal_compliance': return ['our compliance', 'a']
    case '': return ['— NOT STATED, and this is never defaulted', 'r']
    default: return [l.source, 'n']
  }
}

function ruleRow(r: Rule): readonly Cell[] {
  return [
    scopeCell(r.scope),
    r.methodCode,
    directionCell(r.direction),
    enabledCell(r),
    liveCell(r.window),
    r.window.effectiveFrom === '' ? '—' : day(r.window.effectiveFrom),
    authorCell(r.window),
    closedCell(r.window),
  ]
}

function lockRow(l: Lock): readonly Cell[] {
  return [
    scopeCell(l.scope),
    l.methodCode,
    directionCell(l.direction),
    sourceCell(l),
    liveCell(l.window),
    l.window.effectiveFrom === '' ? '—' : day(l.window.effectiveFrom),
    authorCell(l.window),
    closedCell(l.window),
    l.liftPermission === '' ? '— not stated' : l.liftPermission,
  ]
}

/** What both reads carry, and what only the lock read carries. */
const SHARED_NOTES: readonly (readonly [key: string, kicker: string, tone: 'a' | 'n' | 'r'])[] = [
  ['layer_note', 'Two layers, and they must not be merged', 'r'],
  ['resolution_note', 'This is not an answer about what a customer may do', 'r'],
  ['default_note', 'An absent row is a refusal, not an unknown', 'a'],
  ['history_note', 'Closed rows are evidence, not clutter', 'n'],
  ['control_note', 'Reading a lock is not lifting one', 'n'],
]

/**
 * What the screen shows, from both reads.
 *
 * Locks are rendered FIRST. Law 16 says compliance locks outrank ops settings
 * *visibly*, and on a screen with two tables the visible ordering is the
 * ordering: an operator who reads the ops rules first and stops has read the
 * layer that does not bind.
 */
export function composeEntitlementMatrix(
  rulesPayload: Record<string, unknown> | null,
  locksPayload: Record<string, unknown> | null,
  readAt: Date,
): ScreenData {
  const rules: Rule[] = []
  const locks: Lock[] = []
  let unreadable = 0

  const rawRules = rulesPayload !== null && Array.isArray(rulesPayload['rules'])
    ? (rulesPayload['rules'] as readonly unknown[])
    : []
  for (const entry of rawRules) {
    const parsed = readRule(entry)
    if (parsed === null) unreadable += 1
    else rules.push(parsed)
  }

  const rawLocks = locksPayload !== null && Array.isArray(locksPayload['locks'])
    ? (locksPayload['locks'] as readonly unknown[])
    : []
  for (const entry of rawLocks) {
    const parsed = readLock(entry)
    if (parsed === null) unreadable += 1
    else locks.push(parsed)
  }

  const liveLocks = locks.filter((l) => l.window.live === true).length
  const rulesMore = rulesPayload?.['has_more'] === true
  const locksMore = locksPayload?.['has_more'] === true

  const blocks: Block[] = []

  // Locks first — law 16's "visibly" is an ordering on a screen with two tables.
  const lockTable: TableBlock = {
    t: 'table',
    title: 'Compliance locks — these outrank everything below',
    src: 'ledger',
    facets: false,
    cols: LOCK_COLS,
    rows: locks.map(lockRow),
    footer: locksPayload === null
      ? 'the lock read did not answer — see the notice below'
      : `${String(locks.length)} lock(s)${liveLocks === locks.length ? '' : ` · ${String(liveLocks)} in force`}${locksMore ? ' — and more beyond this page' : ''}`,
    foot2:
      'A lock has no “permits” column and cannot grow one: there is no value anybody can write, at any role, that makes a lock grant something. Any live lock at any scope refuses, however specific an operational rule below is.',
  }
  blocks.push(lockTable)

  if (locksPayload === null) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'The layer that outranks the other did not answer',
      title: 'The compliance-lock read failed, so this screen is showing the ops layer alone',
      body: 'That is the more dangerous half to be missing. The rules below can be read as permitting something a lock refuses, and there is no way from here to tell whether one is in force. Treat the table below as incomplete rather than as the answer.',
      ref: 'GET /v1/admin/compliance-locks did not answer',
    })
  } else if (locks.length === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'No locks',
      title: 'No compliance lock has ever been imposed within this query',
      body: 'Closed locks are returned as well as live ones, so an empty table here means none was ever recorded — not that none is in force now. Narrowing the query is how a caller opts out of the history; this read did not.',
      ref: 'compliance_locks · nothing matched',
    })
  } else if (liveLocks > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Law 16',
      title: `${String(liveLocks)} compliance lock(s) are in force and outrank every rule below`,
      body: 'Whatever an operational rule says about those method and direction pairs, the lock refuses them. Lifting one is a separate act under its own permission, named on each row — and reading who lifted a past lock is deliberately available more widely than lifting one, because "why can I withdraw now when I could not last week" is a support question a week later.',
      ref: 'live locks are the binding layer',
    })
  }

  const ruleTable: TableBlock = {
    t: 'table',
    title: 'Operational entitlement rules',
    src: 'ledger',
    facets: false,
    cols: RULE_COLS,
    rows: rules.map(ruleRow),
    footer: rulesPayload === null
      ? 'the rule read did not answer — see the notice below'
      : `${String(rules.length)} rule(s)${rulesMore ? ' — and more beyond this page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'A pair with no rule governing the instant is DISABLED, not undecided — so the gaps in this table are answers too. Closed rows stay: deleting one would destroy the evidence that a restriction was ever imposed.',
  }
  blocks.push(ruleTable)

  if (rulesPayload === null) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The operational entitlement rules could not be read',
      body: 'Nothing is drawn in that table. An empty rule table would have meant every pair resolves to disabled, which is a real and different answer — so this is stated rather than left to look like one.',
      ref: 'GET /v1/admin/entitlements did not answer',
    })
  }

  // The four shared notes come from whichever read answered. They are identical
  // constants on both responses by construction, so the rule read is preferred
  // and the lock read fills in when it did not answer.
  const notesFrom = rulesPayload ?? locksPayload ?? {}
  const lockNotes = locksPayload ?? {}
  for (const [key, kicker, tone] of SHARED_NOTES) {
    const body = str(notesFrom[key]) === '' ? str(lockNotes[key]) : str(notesFrom[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} row(s) could not be read`,
      body: 'Counted rather than dropped silently. A row without a method code names no cell of the matrix, and a matrix quietly one row shorter than the record is one an operator reads as more permissive or more restrictive than it is, with no way to tell which.',
      ref: 'the count is the finding',
    })
  }

  const missing: string[] = []
  if (rulesPayload === null) missing.push('every operational entitlement rule')
  if (locksPayload === null) missing.push('every compliance lock — the layer that outranks the rules')
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (rulesMore) missing.push('the rest of the rules beyond this page')
  if (locksMore) missing.push('the rest of the locks beyond this page')
  // Always. This is the fact the screen most needs an operator not to assume.
  missing.push('what any one customer may actually do — that resolution happens on the money path and is deliberately not recomputed here')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  const subject =
    `${String(locks.length)} lock(s) · ${String(rules.length)} rule(s), not a resolution`
  return { blocks, provenance, subject }
}

// The write controls address a CELL rather than a row id, so they need the
// scope vocabulary and nothing else from here.
export type { Lock, Rule, Scope, Window }

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}

/**
 * The screen: both reads, then the three controls.
 *
 * # The lift control's requirement comes from a lock that is actually on screen
 *
 * `lift_permission` is served per lock, so the requirement is a property of the
 * ROW rather than of the screen — which is the point of it being served at all:
 * this console never models the role nest, it reads the string the backend put
 * on the lock and asks the session whether it holds it.
 *
 * Where the read returned no lock there is no row to read one from, and the
 * fallback is what the endpoint enforces — with the control drawn DEAD, per law
 * 18. An unread requirement is not a satisfied one.
 *
 * Where the locks disagree, no single control can be gated correctly. That is
 * not a state this backend produces, so it is reported rather than resolved by
 * taking the first row.
 */
export const loadEntitlementScreen: ScreenLoader = async (api) => {
  let refusalRef: string | null = null
  const capture = async (path: string): Promise<Record<string, unknown> | null> => {
    try {
      const payload = await api.get<unknown>(path)
      return typeof payload === 'object' && payload !== null
        ? (payload as Record<string, unknown>)
        : null
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      if (error instanceof ApiError && error.decisionRef !== null) refusalRef = error.decisionRef
      return null
    }
  }

  const [rules, locks, caller] = await Promise.all([
    capture(ENTITLEMENT_RULES_PATH),
    capture(COMPLIANCE_LOCKS_PATH),
    callerBeside(api),
  ])

  const data = composeEntitlementMatrix(rules, locks, new Date())
  const required = liftRequirement(locks)

  const blocks: Block[] = [...data.blocks]
  if (refusalRef !== null) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Refused',
      title: 'At least one of the two reads was refused, and here is its reference',
      body: 'Quote this in the decision explorer. It is the only thing that says on what basis the refusal was made, and it is on its own line so it can be copied without the sentence around it.',
      ref: `decision_ref ${String(refusalRef)}`,
    })
  }
  if (required.disagreed) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'The locks disagree about who may lift them',
      title: 'Locks on this screen name different permissions for lifting',
      body: `${required.seen.join(', ')}. Every lock is served the same constant by this backend, so a disagreement means the requirement is not a property of this screen and no single control can be gated correctly. The control below is gated on the first of these alphabetically rather than on whichever row happened to come back first, and this notice is the finding.`,
      ref: 'lift_permission differs between rows',
    })
  }

  return { ...data, blocks: [...blocks, ...entitlementControls(api, caller, required.permission)] }
}

/**
 * What the lift control is gated on, read from the locks that came back.
 *
 * `''` when no lock named one — which `liftLockForm` turns into the endpoint's
 * own requirement and a dead button, never into an enabled one.
 */
export function liftRequirement(
  locksPayload: Record<string, unknown> | null,
): { readonly permission: string; readonly disagreed: boolean; readonly seen: readonly string[] } {
  const raw = locksPayload !== null && Array.isArray(locksPayload['locks'])
    ? (locksPayload['locks'] as readonly unknown[])
    : []
  const seen = [...new Set(
    raw
      .map((entry) => readLock(entry))
      .filter((l): l is Lock => l !== null)
      .map((l) => l.liftPermission)
      .filter((p) => p !== ''),
  )].sort()

  return { permission: seen[0] ?? '', disagreed: seen.length > 1, seen }
}
